// JoinFamilyPage.js
// Lets user join an existing family using a join code

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';

function JoinFamilyPage({ user, userProfile, onFamilyJoined }) {
  // Form state - the join code entered by user
  const [joinCode, setJoinCode] = useState('');

  // Error and loading states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    // Clean up the join code (uppercase, remove extra spaces)
    const cleanCode = joinCode.trim().toUpperCase();

    if (cleanCode.length < 4) {
      setError('Please enter a valid join code.');
      setLoading(false);
      return;
    }

    try {
      // Search for a family with this join code
      const familiesRef = collection(db, 'families');
      const q = query(familiesRef, where('joinCode', '==', cleanCode));
      const querySnapshot = await getDocs(q);

      // Check if we found a family
      if (querySnapshot.empty) {
        setError('No family found with this code. Please check and try again.');
        setLoading(false);
        return;
      }

      // Get the family document
      const familyDoc = querySnapshot.docs[0];
      const familyId = familyDoc.id;
      const familyData = familyDoc.data();

      // Check if user is already a member
      if (familyData.members.includes(user.uid)) {
        setError('You are already a member of this family.');
        setLoading(false);
        return;
      }

      // Get user's first name from their profile
      // Use firstName or email as fallback
      const userDisplayName = userProfile?.firstName || user.email.split('@')[0];

      // Add user to the family's members array and memberNames object
      await updateDoc(doc(db, 'families', familyId), {
        members: arrayUnion(user.uid),
        [`memberNames.${user.uid}`]: userDisplayName
      });

      // Update user's profile with the family ID
      await updateDoc(doc(db, 'users', user.uid), {
        familyId: familyId
      });

      // Notify App.js to refresh user profile
      if (onFamilyJoined) {
        onFamilyJoined();
      }

      // Redirect to todos
      navigate('/todos');
    } catch (err) {
      console.error('Error joining family:', err);
      setError('Failed to join family. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="family-page">
      <button
        onClick={() => navigate('/family-setup')}
        className="button button-secondary"
        style={{ marginBottom: 'var(--spacing-lg)', width: 'auto', maxWidth: '200px' }}
      >
        ← Back
      </button>

      <h1>Join your family</h1>
      <p>Enter the code shared by your partner</p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="joinCode">Family code</label>
          <input
            type="text"
            id="joinCode"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="e.g., KANA-A1B2"
            required
            style={{ textTransform: 'uppercase', letterSpacing: '2px' }}
          />
        </div>

        {error && <p className="form-error">{error}</p>}

        <button
          type="submit"
          className="button button-primary"
          disabled={loading}
        >
          {loading ? 'Joining...' : 'Join family'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
        <a
          href="/create-family"
          onClick={(e) => {
            e.preventDefault();
            navigate('/create-family');
          }}
          style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 600 }}
        >
          Need to create a family instead?
        </a>
      </p>
    </div>
  );
}

export default JoinFamilyPage;
